import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fmtErr } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LOGO_URL } from "@/components/Header";

const empty = { name: "", email: "", password: "", tingkatan: "", tahun_lulus: "", desa: "", rt: "", rw: "", kecamatan: "", kabupaten: "", provinsi: "", aktivitas: "", wa: "" };

export default function RegisterPage() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      toast.success(t("common.saved"));
      navigate("/dashboard");
    } catch (err) {
      setError(fmtErr(err));
    }
    setBusy(false);
  };

  const field = (key, labelKey, opts = {}) => (
    <div key={key} className={opts.className || ""}>
      <Label htmlFor={`reg-${key}`} className="text-sm font-medium text-slate-700">{t(labelKey)}</Label>
      <Input
        id={`reg-${key}`}
        data-testid={`register-${key}-input`}
        type={opts.type || "text"}
        value={form[key]}
        onChange={set(key)}
        required={opts.required !== false}
        className="mt-1.5"
      />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14" data-testid="register-page">
      <div className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8 sm:p-10">
        <img src={LOGO_URL} alt="Al Ibtisam" className="h-24 w-auto mb-6" />
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-emerald-950">{t("alumni.register_title")}</h1>
        <p className="text-sm text-slate-600 mt-2 mb-8">{t("alumni.register_sub")}</p>

        <form onSubmit={submit} className="space-y-5" data-testid="alumni-register-form">
          {field("name", "auth.name")}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {field("email", "auth.email", { type: "email" })}
            {field("password", "auth.password", { type: "password" })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm font-medium text-slate-700">{t("alumni.tingkatan")}</Label>
              <Select value={form.tingkatan} onValueChange={(v) => setForm({ ...form, tingkatan: v })} required>
                <SelectTrigger className="mt-1.5" data-testid="register-tingkatan-select">
                  <SelectValue placeholder="RA / MI / MTs / MA" />
                </SelectTrigger>
                <SelectContent>
                  {["RA", "MI", "MTs", "MA"].map((tk) => (
                    <SelectItem key={tk} value={tk} data-testid={`register-tingkatan-${tk}`}>{tk}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {field("tahun_lulus", "alumni.tahun_lulus")}
          </div>

          <div className="rounded-lg bg-emerald-50/70 border border-emerald-900/10 p-5">
            <p className="text-sm font-semibold text-emerald-900 mb-4">{t("alumni.domisili")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {field("desa", "alumni.desa")}
              {field("rt", "alumni.rt", { required: false })}
              {field("rw", "alumni.rw", { required: false })}
              {field("kecamatan", "alumni.kecamatan")}
              {field("kabupaten", "alumni.kabupaten")}
              {field("provinsi", "alumni.provinsi")}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {field("aktivitas", "alumni.aktivitas")}
            {field("wa", "alumni.wa")}
          </div>

          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}

          <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 h-11" data-testid="register-submit-button">
            {busy ? t("common.loading") : t("auth.daftar")}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {t("alumni.registered")}{" "}
            <Link to="/masuk" className="font-semibold text-emerald-800 hover:underline" data-testid="register-login-link">
              {t("auth.masuk")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
